import type { CSSProperties } from "react";

export const CREATOR_COL_RE = /^(creator_id|creator_name|handle|username|nickname|tiktok_handle)$/i;
export const VIDEO_ID_COL_RE = /^(video_id|tiktok_video_id|vid_id|videoid)$/i;

export const LINK_STYLE: CSSProperties = {
  color: "var(--color-brand-600)",
  textDecoration: "none",
  fontWeight: 500,
};

export function isCreatorColumn(col: string): boolean {
  return CREATOR_COL_RE.test(col.toLowerCase());
}

export function isVideoIdColumn(col: string): boolean {
  return VIDEO_ID_COL_RE.test(col.toLowerCase());
}

// Strip any leading @ so we never produce @@handle in the URL
export function normaliseHandle(raw: string): string {
  return String(raw).replace(/^@+/, "").trim();
}

// A handle is linkable only if it looks like a real TikTok handle, not an
// opaque warehouse id (CID-… or all-digit creator_id surrogate).
export function isLinkableHandle(value: string): boolean {
  const h = normaliseHandle(value);
  if (!h) return false;
  if (/^cid-/i.test(h)) return false;
  if (/^\d+$/.test(h)) return false;
  return /^[A-Za-z0-9._]+$/.test(h);
}

export function profileUrl(handle: string): string {
  return `https://www.tiktok.com/@${normaliseHandle(handle)}`;
}

export function videoUrl(id: string, handle?: string): string {
  const h = handle ? normaliseHandle(handle) : "";
  return h ? `https://www.tiktok.com/@${h}/video/${id}` : `https://www.tiktok.com/video/${id}`;
}

export type LinkEntities = {
  creators: Map<string, string>; // lowercased handle -> profile url
  videos: Map<string, string>; // video id -> video url
};

export type LinkSegment =
  | { kind: "plain"; text: string }
  | { kind: "creator"; text: string; url: string }
  | { kind: "video"; text: string; url: string };

type Stepish = { columns: string[]; rows: Record<string, unknown>[] };

export function collectLinkEntities(steps: Stepish[]): LinkEntities {
  const creators = new Map<string, string>();
  const videos = new Map<string, string>();
  for (const stepEntry of steps ?? []) {
    const cols = stepEntry?.columns ?? [];
    const creatorCols = cols.filter(isCreatorColumn);
    const videoCols = cols.filter(isVideoIdColumn);
    if (creatorCols.length === 0 && videoCols.length === 0) continue;
    for (const row of stepEntry?.rows ?? []) {
      let rowHandle = "";
      for (const c of creatorCols) {
        const v = normaliseHandle(String(row[c] ?? ""));
        if (isLinkableHandle(v)) {
          if (!rowHandle) rowHandle = v;
          creators.set(v.toLowerCase(), profileUrl(v));
        }
      }
      for (const c of videoCols) {
        const id = String(row[c] ?? "").trim();
        if (/^\d{6,}$/.test(id)) videos.set(id, videoUrl(id, rowHandle || undefined));
      }
    }
  }
  return { creators, videos };
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Split text into ordered segments, linking only the exact handles (optionally
// @-prefixed) and video ids present in `entities`. Longest-match-first,
// non-overlapping, with boundary checks so we never match inside a larger token.
export function findLinkMatches(text: string, entities: LinkEntities): LinkSegment[] {
  const handleKeys = [...entities.creators.keys()].sort((a, b) => b.length - a.length);
  const videoKeys = [...entities.videos.keys()].sort((a, b) => b.length - a.length);
  if (handleKeys.length === 0 && videoKeys.length === 0) return [{ kind: "plain", text }];

  const alts: string[] = [];
  if (videoKeys.length) alts.push(videoKeys.map(escapeRegex).join("|"));
  if (handleKeys.length) alts.push("@?(?:" + handleKeys.map(escapeRegex).join("|") + ")");
  const re = new RegExp("(" + alts.join("|") + ")", "gi");

  const segs: LinkSegment[] = [];
  let last = 0;
  re.lastIndex = 0;
  for (let m = re.exec(text); m !== null; m = re.exec(text)) {
    const matched = m[0];
    const start = m.index;
    const end = start + matched.length;
    const before = text[start - 1] ?? "";
    const after = text[end] ?? "";
    const isVid = /^\d+$/.test(matched);
    const badBoundary = isVid
      ? /[0-9]/.test(before) || /[0-9]/.test(after)
      : /[A-Za-z0-9_.@]/.test(before) || /[A-Za-z0-9_.]/.test(after);

    let url: string | undefined;
    let kind: "creator" | "video" | undefined;
    if (isVid) {
      url = entities.videos.get(matched);
      if (url) kind = "video";
    } else {
      url = entities.creators.get(matched.replace(/^@+/, "").toLowerCase());
      if (url) kind = "creator";
    }

    if (badBoundary || !url || !kind) continue; // leave for the next plain slice
    if (start > last) segs.push({ kind: "plain", text: text.slice(last, start) });
    segs.push({ kind, text: matched, url });
    last = end;
  }
  if (last < text.length) segs.push({ kind: "plain", text: text.slice(last) });
  return segs.length ? segs : [{ kind: "plain", text }];
}
